import type { Ref } from 'react';
import { Combobox, Field, Input, Option, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { filterCountryOptions, isAssignedCountry, normalizeCountry } from '@/utils/countryCodes';
import type { AgentAddressFieldName } from '../registration';

const FIELDS = {
  street: { label: 'Street address', autoComplete: 'street-address' },
  city: { label: 'City', autoComplete: 'address-level2' },
  state: { label: 'State or province', autoComplete: 'address-level1' },
  zip: { label: 'ZIP or postal code', autoComplete: 'postal-code' },
  country: { label: 'Country', autoComplete: 'country' },
};

const useStyles = makeStyles({
  country: { width: '100%', minWidth: 0, boxSizing: 'border-box' },
  listbox: { maxHeight: '240px', overflowY: 'auto' },
  fullWidth: { gridColumn: '1 / -1' },
  readOnlyControl: {
    ...shorthands.borderColor(tokens.colorNeutralStroke2),
    ':hover,:focus-within': {
      ...shorthands.borderColor(tokens.colorNeutralStroke2),
    },
    '& input': { cursor: 'default' },
  },
});

export function AgentAddressField({
  field, value, onChange, onBlur, inputRef, error, readOnly = false, className,
}: {
  field: AgentAddressFieldName;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  inputRef?: Ref<HTMLInputElement>;
  error?: string;
  readOnly?: boolean;
  className?: string;
}) {
  const styles = useStyles();
  const metadata = FIELDS[field];
  const options = field === 'country' ? filterCountryOptions(value) : [];
  return (
    <Field
      className={mergeClasses(field === 'street' ? styles.fullWidth : undefined, className)}
      label={metadata.label}
      hint={field === 'street'
        ? 'Optional. Leave all address fields blank to use the agency address. Use your agency\'s address, not your home address.'
        : field === 'country' && !readOnly
          ? 'Two-letter ISO code. Type a code or name, then pick from the list.'
          : undefined}
      validationState={error ? 'error' : 'none'}
      validationMessage={error}
    >
      {field === 'country' && !readOnly ? (
        <Combobox
          className={styles.country}
          listbox={{ className: styles.listbox }}
          freeform
          autoComplete={metadata.autoComplete}
          input={{ ref: inputRef, maxLength: 256 }}
          value={value}
          selectedOptions={isAssignedCountry(value) ? [normalizeCountry(value)] : []}
          onChange={(event) => onChange(event.target.value)}
          onOptionSelect={(_, data) => { if (data.optionValue) onChange(data.optionValue); }}
          onBlur={() => {
            if (isAssignedCountry(value)) onChange(normalizeCountry(value));
            onBlur?.();
          }}
        >
          {options.length > 0 ? options.map(({ code, name }) => (
            <Option key={code} value={code} text={code}>{`${code} - ${name}`}</Option>
          )) : <Option disabled value="" text="">No matching country</Option>}
        </Combobox>
      ) : (
        <Input
          appearance={readOnly ? 'filled-darker' : 'outline'}
          className={readOnly ? styles.readOnlyControl : undefined}
          input={{ ref: inputRef }}
          value={value}
          readOnly={readOnly}
          autoComplete={metadata.autoComplete}
          maxLength={field === 'zip' ? 20 : 256}
          onChange={(_, data) => onChange(data.value)}
          onBlur={onBlur}
        />
      )}
    </Field>
  );
}
